import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import ReactEcharts, { EChartsOption } from "echarts-for-react";
import { useAppSelector } from "../../../../app/hooks";
import { NoChartData } from "../../shared/NoChartData";
import {
	tooltipMark,
	formatNumberOneDecimal,
	miwChartColors,
	utahChartColors,
} from "../../shared/reportUtil";
import {
	STUDENT_CR_PROMPT_ANALYSIS,
	STUDENT_PROMPT_ANALYSIS,
} from "../../student/prompt/PromptReport";
import { WriterDraftScoreModel, PromptScoreModel } from "./PromptUsageByCourseAndPromptViewModel";

interface PromptAnalysisChartData {
	name: string;
	value: number | null;
	link: string;
}

function initializeOptions(
	label: string,
	chartData: PromptAnalysisChartData[],
	average?: number,
	maxValue?: number
): EChartsOption {
	const { config } = useAppSelector((state) => state.site);
	const { foregroundColor, chartPalette, accentColor } = useAppSelector(
		(state) => state.contrast
	);
	let chartColors = config?.isUtahComposeSite ? utahChartColors : miwChartColors;
	if (chartPalette) {
		chartColors = chartPalette.slice(0, 2);
	}

	const option: EChartsOption = {
		textStyle: {
			color: foregroundColor,
			fontFamily: "Montserrat",
			fontWeight: "500",
		},
		grid: {
			containLabel: true,
			top: 40,
			left: 10,
			bottom: 25,
		},
		xAxis: {
			type: "value",
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
			name: label,
			nameLocation: "middle",
			nameGap: 30,
			min: 0,
			max: maxValue,
			splitLine: {
				show: true,
				lineStyle: {
					color: accentColor,
				},
			},
		},
		yAxis: {
			type: "category",
			data: chartData.map((d) => d.name),
			inverse: true,
			axisLabel: {
				padding: 25,
				lineHeight: 15,
				interval: 0,
				width: 180,
				overflow: "break",
				hideOverlap: false,
			},
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
		},
		tooltip: {
			trigger: "item",
			formatter: (params: { value: string; name: string; color: string }) => {
				const heading = `<div>${params.name}</div>`;
				const lineItem = `<div>${tooltipMark(params.color)} ${label}: <b>${params.value}</b></div>`;
				const avgLine = `<div>${tooltipMark("red")} Class Average Most Recent Score: <b>${average}</b></div>`;
				return heading + lineItem + avgLine;
			},
			textStyle: {
				fontSize: 12,
			},
		},
		legend: {
			data: [
				{
					name: "Most Recent Draft Score",
					itemStyle: {
						color: chartColors[0],
					},
				},
				{
					name: "Class Average",
					icon: "circle",
				},
			],
			show: true,
			padding: 10,
			itemGap: 20,
			top: 0,
			textStyle: {
				color: foregroundColor,
			},
		},
		animationDurationUpdate: 500,
		series: [
			{
				type: "bar",
				id: "category",
				name: label,
				data: chartData.map((d) => ({ value: d.value, link: d.link })),
				itemStyle: {
					color: function (param: { dataIndex: number }) {
						return chartColors[param.dataIndex % chartColors.length];
					},
				},
				label: {
					show: true,
					position: ["100%", "50%"],
					height: 25,
					width: 25,
					align: "center",
					verticalAlign: "middle",
					padding: 4,
					backgroundColor: "#fff",
					borderRadius: 25,
					borderColor: "inherit",
					borderWidth: 1,
					fontWeight: "bolder",
					fontSize: 12,
				},
				barMinWidth: 40,
				emphasis: {
					itemStyle: {
						shadowBlur: 10,
					},
				},
				universalTransition: {
					enabled: true,
					divideShape: "clone",
				},
			},
			{
				name: "Class Average",
				type: "line",
				color: "red",
				symbol: "none",
				markLine: {
					name: "Class Average Line",
					data: [
						{
							xAxis: average,
						},
					],
					label: {
						show: false,
					},
					lineStyle: {
						color: "red",
					},
					symbol: ["circle", "circle"],
					silent: true,
				},
			},
		],
	};
	return option;
}

interface ScoresByPromptChartImplProps {
	label: string;
	data: PromptAnalysisChartData[];
	average?: number;
	maxValue?: number;
}

export function ScoresByPromptChartImpl(props: ScoresByPromptChartImplProps) {
	const chartRef = useRef<ReactEcharts>(null);
	const navigate = useNavigate();

	const options = initializeOptions(props.label, props.data, props.average, props.maxValue);
	const chartHeight = props.data.length * 60 + 95;

	const onChartClick = (params: any) => {
		if (params.data.link) navigate(params.data.link);
	};

	useEffect(() => {
		// the event handler ReactEcharts provides interferes with
		// transitions when data in the chart changes.
		if (chartRef?.current) {
			const instance = chartRef.current.getEchartsInstance();
			instance.on("click", onChartClick);
		}
	}, [chartRef]);

	if (!props.data || props.data.length === 0)
		return (
			<NoChartData
				height={chartHeight}
				label={`No ${props.label.toLowerCase()} scores to display.`}
			/>
		);

	return (
		<ReactEcharts
			ref={chartRef}
			option={options}
			className="w-full pl-6"
			style={{
				height: chartHeight,
			}}
			onEvents={{
				click: onChartClick,
			}}
		/>
	);
}

export function ScoresByPromptChart(props: {
	className: string;
	courseId: number;
	writerData: WriterDraftScoreModel[];
	classData: PromptScoreModel;
	isConstructedResponse: boolean;
}) {
	const allData = props.writerData.map((data) => {
		return {
			name: data.writerName,
			value: data.total != null ? Number(data.total) : null,
			link: studentPromptReportLink(
				props.courseId,
				data.writerId,
				props.isConstructedResponse ? STUDENT_PROMPT_ANALYSIS : STUDENT_CR_PROMPT_ANALYSIS
			),
		};
	});
	const data = allData.find((d) => d.value !== null) ? allData : [];
	const average = Number(formatNumberOneDecimal(props.classData.total));
	const maxValue = props.isConstructedResponse ? 3 : 30;

	return (
		<div className={`${props.className}`}>
			<ScoresByPromptChartImpl
				label="Most Recent Draft Score"
				data={data}
				average={average}
				maxValue={maxValue}
			/>
		</div>
	);
}

export function studentPromptReportLink(courseId: number, writerId: number, section?: string) {
	return `/reports/student/prompt?courseId=${courseId}&writerId=${writerId}&section=${section}`;
}
